import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { BUTTON_COLORS, BUTTON_CONFIRM_MAX, buttonOptions } from '@/features/records/utils/buttonField'
import { HEADING_MAX, HELP_TEXT_MAX, RATING_MAX_STARS, RATING_MIN_STARS, STATIC_TEXT_MAX, dividerStyle, headingOptions, pageBreakTitle, ratingStars, staticTextOptions } from '@/features/records/utils/layoutField'
import { DEFAULT_CHOICE_TYPES } from '@/features/records/utils/prefill'
import { useFileUploadConfig } from '@/hooks/useFileUpload'
import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { DEFAULT_MAX_FILES, MAX_FILES_LIMIT, formatBytes, isMultipleFileField } from '@/utils/file-utils'
import { renderSimpleMarkdown } from '@/utils/simpleMarkdown'
const MB = 1024 * 1024

/** Convert MB input to bytes; blank or invalid input clears the constraint. */
function mbToBytes(value: string): number | null {
  const mb = parseFloat(value)
  return Number.isFinite(mb) && mb > 0 ? Math.round(mb * MB) : null
}

/** Convert bytes to a compact MB string for the input value. */
function bytesToMbInput(value: unknown): string {
  return typeof value === 'number' && value > 0 ? String(+(value / MB).toFixed(3)) : ''
}

export function SelectOptionsEditor({ field, onUpdate }: {
  field: FormField
  onUpdate: (id: string, updates: Partial<FormField>) => void
}) {
  const options = field.options
  const selectChoices: string[] = (() => {
    if (!options) return []
    const opts = options as Record<string, unknown>
    if (Array.isArray(opts.choices)) return opts.choices as string[]
    return []
  })()

  const canDefault = DEFAULT_CHOICE_TYPES.includes(field.type)
  const defaultValue = (options as Record<string, unknown> | null)?.defaultValue

  // A renamed default stays the default; a removed one is cleared
  const setChoices = (choices: string[], nextDefault = defaultValue) => {
    onUpdate(field.id, { options: { ...(options ?? {}), choices, defaultValue: canDefault ? nextDefault : null } })
  }

  return (
    <div className="mt-2 space-y-2">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
        Options
      </Label>
      <div className="space-y-1.5">
        {selectChoices.map((choice, idx) => {
          const isDefault = canDefault && choice !== '' && choice === defaultValue
          return (
          <div key={idx} className="flex items-center gap-1.5">
            <Input
              value={choice}
              onChange={(e) => {
                const next = [...selectChoices]
                next[idx] = e.target.value
                setChoices(next, isDefault ? e.target.value : defaultValue)
              }}
              placeholder={`Option ${idx + 1}`}
              className="h-7 text-sm"
            />
            {canDefault && (
              <button
                type="button"
                aria-pressed={isDefault}
                disabled={!choice}
                title={isDefault ? 'Default choice for new records. Click to remove.' : 'Make this the default choice'}
                onClick={() => setChoices(selectChoices, isDefault ? null : choice)}
                className={cn(
                  'h-7 shrink-0 rounded-md border px-2 text-[11px] font-medium transition-colors disabled:opacity-40',
                  isDefault ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted',
                )}
              >
                Default
              </button>
            )}
            <Button
              variant="ghost" size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground/50 hover:text-destructive"
              aria-label={`Remove option ${idx + 1}`}
              onClick={() => setChoices(selectChoices.filter((_, i) => i !== idx), isDefault ? null : defaultValue)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          )
        })}
        <Button
          variant="outline" size="sm" className="h-7 w-full text-xs gap-1.5"
          onClick={() => setChoices([...selectChoices, ''])}
        >
          <Plus className="h-3 w-3" /> Add option
        </Button>
      </div>
    </div>
  )
}


export function FieldConstraints({ field, onUpdate }: {
  field: FormField
  onUpdate: (id: string, updates: Partial<FormField>) => void
}) {
  const validations = (field.validations ?? {}) as Record<string, unknown>
  const { data: fileConfig } = useFileUploadConfig()

  const setValidation = (key: string, value: unknown) => {
    const next = { ...validations }
    if (value === '' || value === null || value === undefined) {
      delete next[key]
    } else {
      next[key] = value
    }
    onUpdate(field.id, { validations: Object.keys(next).length ? next : null })
  }

  const getStr = (key: string) => (validations[key] as string) ?? ''
  const getNumStr = (key: string) =>
    validations[key] != null ? String(validations[key]) : ''
  const setOption = (changes: Record<string, unknown>) => onUpdate(field.id, { options: { ...(field.options ?? {}), ...changes } })

  const constraintLabel = (text: string) => (
    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 block">
      {text}
    </Label>
  )

  switch (field.type) {
    case FormFieldType.TEXT:
    case FormFieldType.PARAGRAPH:
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            {constraintLabel('Min Length')}
            <Input
              type="number" min={0} placeholder="e.g. 2"
              value={getNumStr('minLength')}
              onChange={(e) =>
                setValidation('minLength', e.target.value === '' ? null : parseInt(e.target.value, 10))
              }
              className="h-8 text-sm"
            />
          </div>
          <div>
            {constraintLabel('Max Length')}
            <Input
              type="number" min={0} placeholder="e.g. 100"
              value={getNumStr('maxLength')}
              onChange={(e) =>
                setValidation('maxLength', e.target.value === '' ? null : parseInt(e.target.value, 10))
              }
              className="h-8 text-sm"
            />
          </div>
        </div>
      )

    case FormFieldType.EMAIL:
      return (
        <div className="space-y-3">
          {constraintLabel('Pattern (regex)')}
          <Input
            placeholder="e.g. ^.+@company\.com$"
            value={getStr('pattern')}
            onChange={(e) => setValidation('pattern', e.target.value || null)}
            className="h-8 text-sm font-mono"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Leave blank to use the default e-mail format check.
          </p>
        </div>
      )

    case FormFieldType.NUMBER:
    case FormFieldType.CURRENCY:
    case FormFieldType.RANGE:
    case FormFieldType.PERCENT:
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            {constraintLabel('Min')}
            <Input
              type="number" placeholder="e.g. 0"
              value={getNumStr('min')}
              onChange={(e) =>
                setValidation('min', e.target.value === '' ? null : parseFloat(e.target.value))
              }
              className="h-8 text-sm"
            />
          </div>
          <div>
            {constraintLabel('Max')}
            <Input
              type="number" placeholder="e.g. 9999"
              value={getNumStr('max')}
              onChange={(e) =>
                setValidation('max', e.target.value === '' ? null : parseFloat(e.target.value))
              }
              className="h-8 text-sm"
            />
          </div>
        </div>
      )

    case FormFieldType.DATE:
    case FormFieldType.DATETIME:
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            {constraintLabel('Min Date')}
            <Input
              placeholder="today or yyyy-MM-dd"
              value={getStr('minDate')}
              onChange={(e) => setValidation('minDate', e.target.value || null)}
              className="h-8 text-sm"
            />
          </div>
          <div>
            {constraintLabel('Max Date')}
            <Input
              placeholder="today or yyyy-MM-dd"
              value={getStr('maxDate')}
              onChange={(e) => setValidation('maxDate', e.target.value || null)}
              className="h-8 text-sm"
            />
          </div>
        </div>
      )

    case FormFieldType.PHONE:
      return (
        <div className="space-y-3">
          <div>
            {constraintLabel('Default country')}
            <Input
              value={String((field.options as Record<string, unknown> | null)?.defaultRegion ?? '')}
              placeholder="US"
              maxLength={2}
              onChange={(e) => onUpdate(field.id, {
                options: { ...(field.options ?? {}), defaultRegion: e.target.value.toUpperCase().slice(0, 2) },
              })}
              className="h-8 text-sm uppercase"
            />
          </div>
          <div>
            {constraintLabel('Allowed countries (optional)')}
            <Input
              value={Array.isArray((field.options as Record<string, unknown> | null)?.allowedCountries)
                ? ((field.options as Record<string, unknown>).allowedCountries as string[]).join(', ')
                : ''}
              placeholder="US, CA, GB"
              onChange={(e) => {
                const allowedCountries = e.target.value.split(',').map(value => value.trim().toUpperCase()).filter(Boolean)
                onUpdate(field.id, { options: { ...(field.options ?? {}), allowedCountries: allowedCountries.length ? allowedCountries : undefined } })
              }}
              className="h-8 text-sm uppercase"
            />
          </div>
        </div>
      )

    case FormFieldType.SELECT:
    case FormFieldType.RADIO:
    case FormFieldType.CHECKBOX: {
      const choices = (() => {
        const opts = field.options as Record<string, unknown> | null
        if (!opts) return []
        if (Array.isArray(opts.choices)) return (opts.choices as string[]).filter(Boolean)
        if (Array.isArray(opts.values)) return (opts.values as string[]).filter(Boolean)
        return []
      })()
      const allowedValues = Array.isArray(validations.allowedValues)
        ? (validations.allowedValues as string[])
        : []

      if (choices.length === 0) {
        return (
          <p className="text-xs text-muted-foreground italic">
            Define at least one option above before setting allowed values.
          </p>
        )
      }

      return (
        <div>
          {field.type === FormFieldType.CHECKBOX && (
            <div className="mb-3">
              {constraintLabel('Selection mode')}
              <Select
                value={String((field.options as Record<string, unknown> | null)?.selectionMode ?? 'MULTIPLE')}
                onValueChange={(value) => onUpdate(field.id, { options: { ...(field.options ?? {}), selectionMode: value } })}
              >
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MULTIPLE">Multiple selections</SelectItem>
                  <SelectItem value="SINGLE">Single selection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {constraintLabel('Restrict to allowed values')}
          <p className="mb-2 text-[11px] text-muted-foreground">
            When checked, only the selected options can be submitted. Leave all unchecked to allow any option.
          </p>
          <div className="space-y-1.5">
            {choices.map((choice) => (
              <label
                key={choice}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-muted"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded accent-primary"
                  checked={allowedValues.includes(choice)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...allowedValues, choice]
                      : allowedValues.filter((v) => v !== choice)
                    setValidation('allowedValues', next.length ? next : null)
                  }}
                />
                <span className="text-sm text-foreground">{choice}</span>
              </label>
            ))}
          </div>
          {field.type === FormFieldType.CHECKBOX && (
            <div className="grid grid-cols-2 gap-3">
              <div>{constraintLabel('Min selections')}<Input type="number" min={0} className="h-8 text-sm" value={getNumStr('minSelections')} onChange={e => setValidation('minSelections', e.target.value === '' ? null : parseInt(e.target.value, 10))} /></div>
              <div>{constraintLabel('Max selections')}<Input type="number" min={0} className="h-8 text-sm" value={getNumStr('maxSelections')} onChange={e => setValidation('maxSelections', e.target.value === '' ? null : parseInt(e.target.value, 10))} /></div>
            </div>
          )}
        </div>
      )
    }

    case FormFieldType.FILE_UPLOAD: {
      const allowedTypes = Array.isArray(validations.allowedFileTypes)
        ? (validations.allowedFileTypes as string[])
        : []
      const catalog = fileConfig?.allowedTypes ?? []
      const systemMax = fileConfig?.maxFileSizeBytes
      const maxBytes = typeof validations.maxFileSizeBytes === 'number' ? validations.maxFileSizeBytes : null
      const minBytes = typeof validations.minFileSizeBytes === 'number' ? validations.minFileSizeBytes : null
      const maxExceedsSystem = maxBytes != null && systemMax != null && maxBytes > systemMax
      const minAboveMax = minBytes != null && maxBytes != null && minBytes > maxBytes
      const fileOptions = (field.options ?? {}) as Record<string, unknown>
      const multipleFiles = isMultipleFileField(fileOptions)
      const maxFiles = typeof validations.maxFiles === 'number' ? validations.maxFiles : null
      const maxFilesInvalid = maxFiles != null && (maxFiles < 1 || maxFiles > MAX_FILES_LIMIT)

      return (
        <div className="space-y-3">
          <div>
            {constraintLabel('Number of files')}
            <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" role="group" aria-label="Number of files">
              {([['SINGLE', 'Single file'], ['MULTIPLE', 'Multiple files']] as const).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={(mode === 'MULTIPLE') === multipleFiles}
                  onClick={() => onUpdate(field.id, { options: { ...fileOptions, selectionMode: mode } })}
                  className={cn(
                    'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                    (mode === 'MULTIPLE') === multipleFiles
                      ? 'bg-card text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {multipleFiles && (
              <div className="mt-2 max-w-[9rem]">
                {constraintLabel(`Max files (up to ${MAX_FILES_LIMIT})`)}
                <Input
                  type="number" min={1} max={MAX_FILES_LIMIT} placeholder={String(DEFAULT_MAX_FILES)}
                  value={maxFiles ?? ''}
                  onChange={(e) => setValidation('maxFiles', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  className={maxFilesInvalid ? 'h-8 text-sm border-destructive-500' : 'h-8 text-sm'}
                />
                {maxFilesInvalid && (
                  <p className="mt-1 text-[11px] font-medium text-destructive-600">
                    Max files must be between 1 and {MAX_FILES_LIMIT}.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            {constraintLabel('Allowed file types (optional)')}
            <p className="mb-2 text-[11px] text-muted-foreground">
              Leave all unchecked to accept any supported file type.
            </p>
            {catalog.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">Loading file types…</p>
            ) : (
              <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
                {catalog.map((entry) => (
                  <label
                    key={entry.value}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded accent-primary"
                      checked={allowedTypes.includes(entry.value)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...allowedTypes, entry.value]
                          : allowedTypes.filter((value) => value !== entry.value)
                        setValidation('allowedFileTypes', next.length ? next : null)
                      }}
                    />
                    <span className="text-sm text-foreground">{entry.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              {constraintLabel('Min size (MB)')}
              <Input
                type="number" min={0} step="0.1" placeholder="e.g. 0.1"
                value={bytesToMbInput(validations.minFileSizeBytes)}
                onChange={(e) => setValidation('minFileSizeBytes', mbToBytes(e.target.value))}
                className={minAboveMax ? 'h-8 text-sm border-destructive-500' : 'h-8 text-sm'}
              />
            </div>
            <div>
              {constraintLabel('Max size (MB)')}
              <Input
                type="number" min={0} step="0.1" placeholder="e.g. 10"
                value={bytesToMbInput(validations.maxFileSizeBytes)}
                onChange={(e) => setValidation('maxFileSizeBytes', mbToBytes(e.target.value))}
                className={maxExceedsSystem ? 'h-8 text-sm border-destructive-500' : 'h-8 text-sm'}
              />
            </div>
          </div>

          {maxExceedsSystem ? (
            <p className="text-[11px] font-medium text-destructive-600">
              Max size cannot exceed the system limit of {formatBytes(systemMax)}.
            </p>
          ) : minAboveMax ? (
            <p className="text-[11px] font-medium text-destructive-600">
              Min size cannot be greater than max size.
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              {systemMax != null
                ? `System maximum per file: ${formatBytes(systemMax)}.`
                : 'The submitted value will be the stored file reference.'}
            </p>
          )}
        </div>
      )
    }

    case FormFieldType.BUTTON: {
      const button = buttonOptions(field)
      const setButton = (changes: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...(field.options ?? {}), ...changes }
        for (const key of Object.keys(next)) if (next[key] === '' || next[key] === false) delete next[key]
        onUpdate(field.id, { options: Object.keys(next).length ? next : null })
      }
      return (
        <div className="space-y-3">
          <div>{constraintLabel('Button color')}<div className="flex flex-wrap gap-1" role="group" aria-label="Button color">
            {BUTTON_COLORS.map((color) => <button key={color} type="button" aria-pressed={button.buttonColor === color} onClick={() => setButton({ buttonColor: color === 'primary' ? '' : color })} className={cn('rounded-md border px-3 py-1 text-xs capitalize', button.buttonColor === color ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground')}>{color}</button>)}
          </div></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={button.confirm} onCheckedChange={(value) => setButton({ confirm: value })} />Ask for confirmation before running</label>
          {button.confirm && <Input value={button.confirmMessage} maxLength={BUTTON_CONFIRM_MAX} placeholder="Confirmation message" aria-label="Confirmation message" onChange={(e) => setButton({ confirmMessage: e.target.value })} />}
          <label className="flex items-center gap-2 text-sm"><Switch checked={button.oneShot} onCheckedChange={(value) => setButton({ oneShot: value })} />Only once per record</label>
          <p className="text-[11px] text-muted-foreground">Use a “Button clicked” automation trigger to choose what this button does.</p>
        </div>
      )
    }

    case FormFieldType.RATING:
      return <div>{constraintLabel('Number of stars')}<Select value={String(ratingStars(field))} onValueChange={(value) => setOption({ max: Number(value) === 5 ? null : Number(value) })}><SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: RATING_MAX_STARS - RATING_MIN_STARS + 1 }, (_, i) => RATING_MIN_STARS + i).map((count) => <SelectItem key={count} value={String(count)}>{count} stars</SelectItem>)}</SelectContent></Select></div>

    case FormFieldType.SECTION_HEADING: {
      const heading = headingOptions(field)
      return <div className="space-y-3"><div>{constraintLabel('Title')}<Input value={heading.text} maxLength={HEADING_MAX} onChange={(e) => setOption({ text: e.target.value })} placeholder="Section title" /></div><div>{constraintLabel('Subtitle')}<Input value={heading.subtitle} maxLength={HELP_TEXT_MAX} onChange={(e) => setOption({ subtitle: e.target.value || null })} placeholder="Short line under the title" /></div><div>{constraintLabel('Size')}<Select value={heading.size} onValueChange={(value) => setOption({ size: value === 'h2' ? null : value })}><SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="h2">Large</SelectItem><SelectItem value="h3">Medium</SelectItem></SelectContent></Select></div></div>
    }

    case FormFieldType.STATIC_TEXT: {
      const text = staticTextOptions(field)
      return <div className="space-y-3"><div>{constraintLabel('Text')}<Textarea value={text.text} maxLength={STATIC_TEXT_MAX} rows={5} onChange={(e) => setOption({ text: e.target.value })} placeholder="Write form instructions. Markdown is supported." /><div className="mt-2 rounded-md border border-border bg-background p-2 text-sm">{text.text ? renderSimpleMarkdown(text.text) : <span className="text-muted-foreground">Preview</span>}</div></div><div>{constraintLabel('Alignment')}<Select value={text.align} onValueChange={(value) => setOption({ align: value === 'left' ? null : value })}><SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent></Select></div></div>
    }

    case FormFieldType.DIVIDER:
      return <div>{constraintLabel('Style')}<Select value={dividerStyle(field)} onValueChange={(value) => setOption({ style: value === 'solid' ? null : value })}><SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="solid">Solid</SelectItem><SelectItem value="dashed">Dashed</SelectItem></SelectContent></Select></div>

    case FormFieldType.PAGE_BREAK:
      return <div>{constraintLabel('Step title (optional)')}<Input value={pageBreakTitle(field)} maxLength={HEADING_MAX} onChange={(e) => setOption({ title: e.target.value || null })} placeholder="e.g. Contact details" /></div>

    case FormFieldType.EMAIL_MESSAGE:
      return (
        <div>
          {constraintLabel('Search the picker opens with (optional)')}
          <Input
            value={typeof field.options?.defaultSearch === 'string' ? field.options.defaultSearch : ''}
            maxLength={200}
            onChange={(e) => setOption({ defaultSearch: e.target.value || null })}
            placeholder="from:{{email}}"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Use {'{{field_key}}'} to fill in values from the record. A part whose value is empty is left out.
          </p>
        </div>
      )

    case FormFieldType.LINE_BREAK:
      return <p className="text-xs text-muted-foreground">The next field starts on a new row. Nothing is shown on the form.</p>

    default:
      return null
  }
}

